// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { AERIAL_IMAGERY_PROVIDERS } from 'Features/order/OrderForm/Package/DataSource/dataSection';
import { catalogOptionKey } from './catalogOption';

// A cart of catalog products read as the one selection the order form knows (AUT-6630 D6, "start simple"): the
// combined name and the quote's cart figures, the union of included layers and of available add-ons (D13), the first
// product's id and subtype for the legacy readers, and the cart's data classes. One product is itself.

const unionById = (lists: IDeliverable[][]): IDeliverable[] => {
  const seen = new Set<string>();
  return lists.flat().filter((deliverable) => {
    if (seen.has(deliverable._id)) return false;
    seen.add(deliverable._id);
    return true;
  });
};

const sum = (values: (number | undefined)[]): number => Math.round(values.reduce((total: number, value) => total + (value ?? 0), 0) * 100) / 100;

const sameProducts = (ids: string[], others: string[]): boolean => ids.length === others.length && ids.every((id) => others.includes(id));

// The products behind the ticked keys, in list (catalog) order; a bundle key names no product
export const productsForKeys = (groupings: IBundle[], keys: string[]): IBundle[] => (
  groupings.filter((grouping) => !grouping.customBundleId && keys.includes(catalogOptionKey(grouping)))
);

// What the save sends as the cart: every product of a combination, or the one selection's id
export const cartProductIds = (selectedBundle: IBundle): string[] => (
  selectedBundle.productIds?.length ? selectedBundle.productIds : [selectedBundle._id]
);

export const combineCatalogProducts = (products: IBundle[], cart: ICatalogCart | null): IBundle | null => {
  if (!products.length) return null;
  if (products.length === 1) return products[0];

  // Catalog order (D7: Clearances & Violations last): the cart block names it; the list carries it otherwise
  const ids = products.map((product) => product._id);
  const quoted = cart && sameProducts(ids, cart.productIds.map(String)) ? cart : null;
  const ordered = quoted
    ? [...products].sort((a, b) => quoted.productIds.indexOf(a._id) - quoted.productIds.indexOf(b._id))
    : products;
  const [first] = ordered;
  const names = ordered.map((product) => product.name);
  const dataProviders = Array.from(new Set(ordered.map((product) => product.dataProvider).filter((provider): provider is string => !!provider)));
  // One class for the readers that know one (the KML-only gate holds when any class is marketplace aerial, as the
  // server does); the set is for the data step (Chunk 5)
  const dataProvider = dataProviders.find((provider) => AERIAL_IMAGERY_PROVIDERS.includes(provider)) ?? dataProviders[0] ?? null;

  // The quote's cart figures when it priced these products together; the products' own figures summed until it has
  // (browse mode, or a quote that predates the tick): provisional, corrected by the next quote
  const figures = quoted
    ? {
      bundleCost: quoted.afterDiscountDollar ?? 0,
      priceCredit: quoted.afterDiscountCredit ?? 0,
      contactForPricing: !quoted.priceable,
      priceBeforeDiscountDollar: quoted.totalBeforeDiscountDollar ?? 0,
      discountDollar: quoted.discountDollar ?? 0,
      promotionDollar: quoted.promotionDollar ?? 0,
      dollarPerCredit: quoted.dollarPerCredit,
      tierName: quoted.tierName,
      discountPercent: quoted.discountPercent,
      nextTierTeaser: quoted.nextTierTeaser ?? null,
      deploymentFeeDollar: quoted.deploymentFeeDollar ?? 0,
      dataProviderDollar: quoted.dataProviderDollar ?? 0,
      dataProviderCredit: quoted.dataProviderCredit ?? 0,
      extractionDollar: quoted.extractionDollar ?? 0,
      extractionCredit: quoted.extractionCredit ?? 0,
    }
    : {
      bundleCost: sum(ordered.map((product) => product.bundleCost)),
      priceCredit: sum(ordered.map((product) => product.priceCredit)),
      contactForPricing: ordered.some((product) => !!product.contactForPricing),
      priceBeforeDiscountDollar: sum(ordered.map((product) => product.priceBeforeDiscountDollar)),
      discountDollar: sum(ordered.map((product) => product.discountDollar)),
      promotionDollar: sum(ordered.map((product) => product.promotionDollar)),
      dollarPerCredit: first.dollarPerCredit,
      tierName: first.tierName,
      discountPercent: first.discountPercent,
      nextTierTeaser: null,
      deploymentFeeDollar: Math.max(0, ...ordered.map((product) => product.deploymentFeeDollar ?? 0)),
      dataProviderDollar: sum(ordered.map((product) => product.dataProviderDollar)),
      dataProviderCredit: sum(ordered.map((product) => product.dataProviderCredit)),
      extractionDollar: sum(ordered.map((product) => product.extractionDollar)),
      extractionCredit: sum(ordered.map((product) => product.extractionCredit)),
    };

  return {
    ...first,
    name: quoted?.name || names.join(' + '),
    productIds: ordered.map((product) => product._id),
    products: ordered.map((product) => ({ _id: product._id, name: product.name, dataProvider: product.dataProvider ?? null, subtype: product.subtype })),
    bundleDeliverables: unionById(ordered.map((product) => product.bundleDeliverables ?? [])),
    additionalDeliverables: unionById(ordered.map((product) => product.additionalDeliverables ?? [])),
    dataProvider,
    dataProviders,
    customBundleId: undefined,
    parentProductName: undefined,
    contractedPricePerFoot: undefined,
    contractedDollar: undefined,
    sizeBand: first.sizeBand ?? null,
    bufferMultiplier: first.bufferMultiplier ?? 1,
    ...figures,
  };
};

export default combineCatalogProducts;
